"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";

import BirthTimeRectifier from "@/components/engine-visuals/BirthTimeRectifier";
import type { RectificationResponse } from "@/lib/api/rectification";
import RectificationForm from "./RectificationForm";

function KundaliHandoff({ isHi }: { isHi: boolean }) {
  return (
    <div className="mt-8 rounded-2xl border border-border-interactive bg-surface-brand-subtle p-5 text-center">
      <h2 className="mb-1 text-sm font-bold text-text-primary sm:text-base">
        {isHi
          ? "किसी संभावित समय से अपनी संपूर्ण जन्म-कुंडली देखें"
          : "Generate a full Kundali from a candidate time"}
      </h2>
      <p className="mx-auto mb-4 max-w-md text-xs text-text-secondary">
        {isHi
          ? "D9 नवमांश, विंशोत्तरी दशा एवं ग्रह बल की पूर्ण गणना प्राप्त करें।"
          : "See the D9 Navamsha, the Vimshottari timeline, and planetary strengths in full."}
      </p>
      <Link
        href={isHi ? "/hi/free-kundali/" : "/free-kundali/"}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-surface-brand-strong px-6 py-2.5 text-xs font-bold text-text-on-brand shadow-xs transition-all hover:bg-surface-brand-strong-hover sm:text-sm"
      >
        <span>✨</span>
        <span>{isHi ? "कुंडली बनाएं (निःशुल्क)" : "Generate Free Kundali"}</span>
      </Link>
    </div>
  );
}

export default function BirthTimeRectificationClient({
  language = "en",
  crossLinks,
}: {
  language?: "en" | "hi";
  crossLinks?: ReactNode;
}) {
  const isHi = language === "hi";
  const [result, setResult] = useState<RectificationResponse | null>(null);

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-8 text-center">
          <p className="mb-3 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
            {isHi ? "वैदिक जन्म समय परिशोधन" : "Vedic Birth Time Rectification"}
          </p>
          <h1 className="mb-3 font-serif text-2xl font-bold leading-tight text-text-primary sm:text-3xl">
            {isHi
              ? "जन्म समय परिशोधन: अपने जीवन की घटनाओं से लग्न निकालें"
              : "Birth Time Rectification: Narrow Your Lagna From Your Life Events"}
          </h1>
          <p className="mx-auto max-w-lg text-xs leading-relaxed text-text-secondary sm:text-sm">
            {isHi
              ? "जिस समय-सीमा के बारे में आप निश्चित हैं वह दें, फिर विवाह, संतान या नौकरी जैसी तिथियां जोड़ें। इंजन प्रत्येक संभावित लग्न-पाद की विंशोत्तरी दशा आपकी घटनाओं से मिलाकर क्रम देता है।"
              : "Give the window you are sure of, then add dates you remember. The engine ranks each candidate Lagna pada by how well its Vimshottari periods explain those events."}
          </p>
        </div>

        <RectificationForm isHi={isHi} onResult={setResult} />

        {result && <BirthTimeRectifier result={result} locale={isHi ? "hi" : "en"} />}
        {result && <KundaliHandoff isHi={isHi} />}

        {crossLinks}
      </div>
    </div>
  );
}
